import type { BaseAPIResponse } from '~/types/api';
import type { Address, Aka, Person } from '~/types/person/person';
import type { QRData } from '~/types/person/settings';
import type { EmailVerificationData, Phone } from '~/types/person/vault';
import { ApiError } from '~/utils/errors';

// Pure API service for person-related operations - no state management
export const personService = {
    async signPoa(personId: number, signature: string): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(nuxtApp.$api.routes.people.poa.sign(personId), {
                method: 'POST',
                body: {
                    name: signature,
                },
            });

            if (status !== 204) {
                throw new ApiError(message || 'Failed to sign POA agreement', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async retractPoa(personId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(nuxtApp.$api.routes.people.poa.retract(personId), {
                method: 'DELETE',
            });

            if (status !== 204) {
                throw new ApiError(message || 'Failed to retract POA agreement', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getMfaQrCode(): Promise<QRData> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<QRData>>(nuxtApp.$api.routes.me.mfa.get());

        if (status !== 200 || !data) {
            throw new ApiError(message || 'Failed to fetch QR code', status);
        }

        if (!data) {
            throw new ApiError('No QR code data received', 404);
        }

        return data.data;
    },

    async enableMfa(key: string, code: string): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(nuxtApp.$api.routes.me.mfa.enable(), {
                method: 'PATCH',
                body: {
                    key,
                    code,
                },
            });

            if (status !== 204) {
                throw new ApiError(message || 'Failed to enable MFA', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async disableMfa(): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(nuxtApp.$api.routes.me.mfa.disable(), {
                method: 'DELETE',
            });

            if (status !== 204) {
                throw new ApiError(message || 'Failed to disable MFA', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getPerson(personId: number): Promise<Person> {
        const nuxtApp = useNuxtApp();

        // Check if API plugin is available
        if (!nuxtApp.$api || !nuxtApp.$api.routes) {
            throw new ApiError('API plugin not yet initialized', 500);
        }

        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<Person>>(
                nuxtApp.$api.routes.people.person(personId)
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch person data', status);
            }

            return data.data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async updatePerson(personId: number, postData: Partial<Person>): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(nuxtApp.$api.routes.people.person(personId), {
                method: 'PATCH',
                body: postData,
            });

            if (status !== 201) {
                throw new ApiError(message || 'Failed to update person', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async addAka(personId: number, postData: Aka): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(nuxtApp.$api.routes.people.akas.create(personId), {
                method: 'POST',
                body: postData,
            });

            if (status !== 201) {
                throw new ApiError(message || 'Failed to add alias name', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async deleteAka(personId: number, akaId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.akas.delete(personId, akaId),
                {
                    method: 'DELETE',
                }
            );

            if (status !== 204) {
                throw new ApiError(message || 'Failed to delete alias name', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async updateAka(personId: number, akaId: number, postData: Partial<Aka>): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.akas.update(personId, akaId),
                {
                    method: 'PATCH',
                    body: postData,
                }
            );

            if (status !== 201) {
                throw new ApiError(message || 'Failed to update alias name', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async addAddress(personId: number, postData: Address): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.address.create(personId),
                {
                    method: 'POST',
                    body: postData,
                }
            );

            if (status !== 201) {
                throw new ApiError(message || 'Failed to add address', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async updateAddress(personId: number, addressId: number, postData: Partial<Address>): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.address.update(personId, addressId),
                {
                    method: 'PATCH',
                    body: postData,
                }
            );

            if (status !== 201) {
                throw new ApiError(message || 'Failed to update address', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async deleteAddress(personId: number, addressId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.address.delete(personId, addressId),
                {
                    method: 'DELETE',
                }
            );

            if (status !== 204) {
                throw new ApiError(message || 'Failed to delete address', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async deletePhone(personId: number, phoneId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.phones.delete(personId, phoneId),
                {
                    method: 'DELETE',
                }
            );

            if (status !== 204) {
                throw new ApiError(message || 'Failed to delete phone number', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async updatePhone(personId: number, phoneId: number, postData: Partial<Phone>): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.phones.update(personId, phoneId),
                {
                    method: 'PATCH',
                    body: postData,
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to update phone number', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async sendPhoneVerificationCode(
        personId: number,
        postData: { phone_number: string; country_code: string; method: string }
    ): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.phones.sendVerification(personId),
                {
                    method: 'POST',
                    body: postData,
                }
            );

            if (status !== 204) {
                throw new ApiError(message || 'Failed to send phone verification code', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async verifyPhoneOtp(personId: number, payload: { country_code: string; phone_number: string; code: string }): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.phones.create(personId),
                {
                    method: 'POST',
                    body: payload,
                }
            );

            if (status !== 201) {
                throw new ApiError(message || 'Failed to verify phone OTP', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async addPhoneWithoutVerification(personId: number, payload: { country_code: string; phone_number: string }): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.phones.create(personId),
                {
                    method: 'POST',
                    body: {
                        ...payload,
                        skip_verification: true,
                    },
                }
            );

            if (status !== 201) {
                throw new ApiError(message || 'Failed to add phone number', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async sendEmailVerificationCode(email: string): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.emails.sendVerification(),
                {
                    method: 'POST',
                    body: { email },
                }
            );

            if (status !== 204) {
                throw new ApiError(message || 'Failed to send email verification code', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async verifyEmailOtp(personId: number, payload: EmailVerificationData): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.emails.create(personId),
                {
                    method: 'POST',
                    body: payload,
                }
            );

            if (status !== 200 && status !== 201) {
                throw new ApiError(message || 'Failed to verify email OTP', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async updateEmail(personId: number, emailId: number, postData: { primary: boolean }): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.emails.update(personId, emailId),
                {
                    method: 'PATCH',
                    body: postData,
                }
            );

            if (status !== 200 && status !== 204) {
                throw new ApiError(message || 'Failed to update email', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async deleteEmail(personId: number, emailId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi<BaseAPIResponse<null>>(
                nuxtApp.$api.routes.people.emails.delete(personId, emailId),
                {
                    method: 'DELETE',
                }
            );

            if (status !== 200 && status !== 204) {
                throw new ApiError(message || 'Failed to delete email', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async fetchStates(id: number): Promise<any> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message, data } = await usePbApi<BaseAPIResponse<any>>(
                nuxtApp.$api.routes.countries.lookup(id),
                {
                    method: 'GET',
                }
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch states', status);
            }
            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async fetchStatesByCode(countryCode: string): Promise<any> {
        try {
            const { status, message, data } = await usePbApi<BaseAPIResponse<any>>(
                `/countries/${countryCode}`,
                {
                    method: 'GET',
                }
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch states', status);
            }
            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },
};
